"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PortalController = void 0;
// 옮겨진 host는 원래 자리의 트리와 함께 사라지지 않는다. 모든 컨트롤러가 관찰자 하나를 나눠 쓰며,
// 자리표 주석이 문서에서 떨어졌는지(소비자가 DOM에서 빠졌는지)를 살펴 host를 걷어낸다.
const watched = new Set();
let observer;
const releaseOrphans = () => watched.forEach(controller => controller.releaseIfOrphaned());
const watch = (controller, roots) => {
    observer ??= new MutationObserver(releaseOrphans);
    watched.add(controller);
    roots.forEach(root => observer?.observe(root, { childList: true, subtree: true }));
};
const unwatch = (controller) => {
    watched.delete(controller);
    if (watched.size > 0)
        return;
    observer?.disconnect();
    observer = undefined;
};
/**
 * 연결된 동안 host를 index.html의 portal 컨테이너(#portal-root)로 옮겨, 조상의 transform·contain·stacking context에
 * 갇히지 않게 하는 ReactiveController.
 *
 * React의 createPortal과 같은 역할을 하되 host 노드를 통째로 이동하므로,
 * 이동 중 재실행되는 connected/disconnected lifecycle은 moving 플래그로 무시한다.
 * 원래 위치는 anchor 주석으로 표시한다. 이 주석이 문서에서 떨어지면(소비자가 DOM에서 빠지면) 옮겨간 host도 함께 걷어낸다.
 *
 * open 상태가 아니라 connect 생명주기에 이동을 묶은 이유: open이 바뀌는 시점에 이동까지 겹치면
 * "이동 직후 열림"이 같은 프레임에서 처리되어 브라우저가 전환 시작 스타일을 커밋하지 못하고
 * CSS transition이 재생되지 않는다. sheet·toast·popover의 host는 미리 마운트돼 있고 open만 토글되므로,
 * 이동을 connect 시점으로 분리해두면 open 전환 전에 위치가 이미 안정된 상태다.
 * 열릴 때 만들어지는 표면(tooltip 말풍선)은 만들자마자 열리므로 이 방식에 기대지 않고,
 * 만들어지는 순간의 시작 스타일(`@starting-style`)로 열림 전환을 재생한다.
 */
class PortalController {
    constructor(host) {
        this.host = host;
        this.anchor = document.createComment('portal');
        this.portaled = false;
        this.moving = false;
        host.addController(this);
    }
    hostConnected() {
        if (this.portaled || this.moving)
            return;
        this.host.before(this.anchor);
        this.move(() => document.getElementById('portal-root')?.append(this.host));
        this.portaled = true;
        watch(this, this.ancestorRoots);
    }
    /**
     * 옮기기 전 자리의 root(document나 소비자의 shadow root). 자리표 주석이 그곳에 남아 있어,
     * 옮겨진 뒤에도 선언한 트리 안의 요소(aria-controls로 가리키는 트리거 등)를 찾을 때 쓴다.
     */
    get originRoot() {
        const origin = this.portaled ? this.anchor : this.host;
        return origin.getRootNode();
    }
    hostDisconnected() {
        if (this.moving)
            return;
        if (!this.portaled)
            return;
        this.anchor.remove();
        this.portaled = false;
        unwatch(this);
    }
    /** 자리표가 문서에서 떨어졌으면 옮겨간 host를 걷어낸다. 공유 관찰자가 부른다. */
    releaseIfOrphaned() {
        if (!this.portaled || this.anchor.isConnected)
            return;
        this.host.remove();
    }
    // 소비자가 놓인 트리와 그 바깥 shadow 트리들의 root. 어느 단계에서 빠져도 알아채도록 모두 관찰한다.
    get ancestorRoots() {
        const roots = [];
        let root = this.anchor.getRootNode();
        roots.push(root);
        while (root instanceof ShadowRoot) {
            root = root.host.getRootNode();
            roots.push(root);
        }
        return roots;
    }
    // host 이동은 disconnected/connected를 재실행하므로 그 사이 자기 정리를 막는다.
    move(run) {
        this.moving = true;
        run();
        this.moving = false;
    }
}
exports.PortalController = PortalController;
//# sourceMappingURL=data:application/json;base64,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